/**
 * The bulk bar (FR-16): what a selection of up to 200 documents can be asked
 * — **tag** (or untag), **move to a folder**, **move to another workspace**
 * (landing in its top level), **archive**, **download as ZIP** — and from W6
 * **share** (the host's share dialog, for whoever holds `share:can_create`).
 *
 * Each document is gated **on its own** by the service, so a mixed selection
 * comes back with an answer per document; the bar reports which changed and
 * names each one that did not, with the service's reason. It offers only the
 * verbs the reader holds (`bulkVerbs`) — an uploader's own+create tags and
 * moves their own uploads and is told about the rest; a viewer gets the ZIP.
 *
 * The ZIP is two steps (D20): a single-use ticket, then the browser
 * **navigates** to it, so its download manager writes the streamed archive to
 * disk — never a blob held in the tab.
 */
import { Alert, Button, ConfirmDialog, Dialog, RadioGroup, Select, useToast } from '@fiveox/ui'
import { Archive, FolderInput, FolderSymlink, PackageOpen, Share2, Tags, X } from 'lucide-react'
import { useEffect, useState } from 'react'

import {
  MAX_SELECTION,
  MAX_ZIP_BYTES,
  TAG,
  bulkVerbs,
  formatBytes,
  type BulkAction,
  type BulkResult,
  type ProDocClient,
  type ProDocDocument,
  type Verb,
  type Workspace,
} from './prodoc-client'
import { plural } from './bits'
import { FolderPicker } from './folder-dialogs'
import { errorText } from './hooks'
import { TagPicker, type TagSelection, type useTags } from './tag-picker'

/** What a bulk action did, for the register to say: how many changed, and each one that did not, by name. */
export interface BulkReport {
  /** "Tagged", "Moved", "Archived". */
  verb: string
  changed: number
  refused: { id: string; title: string; message: string }[]
}

export interface BulkBarProps {
  client: ProDocClient
  /** The selected documents, as the register last read them. */
  selected: ProDocDocument[]
  can: (resource: string, verb: Verb) => boolean
  tags: ReturnType<typeof useTags>
  workspaces: Workspace[]
  /** The workspace the register is filtered to — where a folder move looks first. */
  currentWorkspace: string | null
  app: string
  onClear: () => void
  /** After the service answered: the report, and the ids it changed (the register re-reads). */
  onDone: (report: BulkReport, changedIds: string[]) => void
  /** Open the ZIP's ticket — the host's window; a test's spy. */
  navigate: (url: string) => void
  /** W6: share the selection — offered only when given (the register gives it to a holder of `share:can_create`). */
  onShare?: () => void
}

type Dialogs = 'tag' | 'folder' | 'workspace' | 'archive' | null

const VERB: Record<BulkAction, string> = {
  tag: 'Tagged',
  untag: 'Untagged',
  move_folder: 'Moved',
  move_workspace: 'Moved',
  archive: 'Archived',
}

/** A bulk answer as a report, each refused document named by its title. */
export function reportOf(action: BulkAction, result: BulkResult, selected: ProDocDocument[]): BulkReport {
  const titles = new Map(selected.map((d) => [d.id, d.title]))
  return {
    verb: VERB[action],
    changed: result.changed,
    refused: result.results
      .filter((r) => !r.ok)
      .map((r) => ({ id: r.document_id, title: titles.get(r.document_id) ?? 'A document', message: r.ok ? '' : errorText(r.error) })),
  }
}

export function BulkBar(props: BulkBarProps) {
  const { client, selected, can, tags, workspaces, currentWorkspace, app, onClear, onDone, navigate, onShare } = props
  const { toast } = useToast()
  const verbs = bulkVerbs(can)
  const [dialog, setDialog] = useState<Dialogs>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [zipping, setZipping] = useState(false)
  useEffect(() => setError(''), [dialog])

  const ids = selected.map((d) => d.id)
  const over = ids.length > MAX_SELECTION
  const bytes = selected.reduce((n, d) => n + (d.size_bytes ?? 0), 0)

  const run = async (action: BulkAction, extra: { tag_ids?: string[]; folder_id?: string | null; workspace_id?: string }) => {
    setBusy(true)
    setError('')
    try {
      const result = await client.documents.bulk({ action, document_ids: ids, ...extra })
      setDialog(null)
      const changedIds = result.results.filter((r) => r.ok).map((r) => r.document_id)
      onDone(reportOf(action, result, selected), changedIds)
    } catch (e) {
      setError(errorText(e))
    } finally {
      setBusy(false)
    }
  }

  const zip = async () => {
    if (bytes > MAX_ZIP_BYTES) {
      toast({ message: `A ZIP holds up to ${formatBytes(MAX_ZIP_BYTES)}; these are ${formatBytes(bytes)}. Select fewer.`, tone: 'error' })
      return
    }
    setZipping(true)
    try {
      const ticket = await client.documents.zip(ids)
      navigate(ticket.url)
      toast('Your ZIP is downloading. Every file in it is recorded as read.')
    } catch (e) {
      toast({ message: errorText(e, 'The ZIP could not be started.'), tone: 'error' })
    } finally {
      setZipping(false)
    }
  }

  if (selected.length === 0) return null

  return (
    <div role="region" aria-label="Selected documents" className="border-primary/30 bg-primary/5 flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2">
      <p className="text-foreground mr-1 text-sm font-medium">{plural(selected.length, 'document')} selected</p>
      <Button variant="ghost" size="sm" onClick={onClear}>
        <X size={14} aria-hidden />
        Clear
      </Button>
      {over && <p className="text-muted-foreground text-xs">Up to {MAX_SELECTION} at a time — clear some to act on them.</p>}
      <span className="flex-1" />
      {verbs.tag && (
        <Button variant="outline" size="sm" onClick={() => setDialog('tag')} disabled={over}>
          <Tags size={14} aria-hidden />
          Tag…
        </Button>
      )}
      {verbs.move && (
        <>
          <Button variant="outline" size="sm" onClick={() => setDialog('folder')} disabled={over}>
            <FolderInput size={14} aria-hidden />
            Move to folder…
          </Button>
          <Button variant="outline" size="sm" onClick={() => setDialog('workspace')} disabled={over || workspaces.length < 2}>
            <FolderSymlink size={14} aria-hidden />
            Move to workspace…
          </Button>
        </>
      )}
      {verbs.archive && (
        <Button variant="outline" size="sm" onClick={() => setDialog('archive')} disabled={over}>
          <Archive size={14} aria-hidden />
          Archive…
        </Button>
      )}
      {onShare && (
        <Button variant="outline" size="sm" onClick={onShare} disabled={over}>
          <Share2 size={14} aria-hidden />
          Share…
        </Button>
      )}
      {verbs.zip && (
        <Button variant="outline" size="sm" onClick={() => void zip()} disabled={over} loading={zipping} loadingText="Preparing…">
          <PackageOpen size={14} aria-hidden />
          Download as ZIP
        </Button>
      )}

      <TagDialog
        open={dialog === 'tag'}
        count={selected.length}
        tags={tags}
        canCreate={can(TAG, 'can_create')}
        busy={busy}
        error={error}
        onClose={() => setDialog(null)}
        onSave={async (mode, value) => {
          setBusy(true)
          setError('')
          try {
            // A bulk tag names tags by id: a new name is made first (`can_create` on `tag`).
            const made = await Promise.all(value.new_names.map((name) => client.tags.create({ name })))
            if (made.length) tags.reload()
            await run(mode, { tag_ids: [...value.tag_ids, ...made.map((t) => t.id)] })
          } catch (e) {
            setError(errorText(e))
            setBusy(false)
          }
        }}
      />
      <MoveFolderDialog
        open={dialog === 'folder'}
        client={client}
        selected={selected}
        workspaces={workspaces}
        currentWorkspace={currentWorkspace}
        app={app}
        busy={busy}
        error={error}
        onClose={() => setDialog(null)}
        onSave={(folderId) => void run('move_folder', { folder_id: folderId })}
      />
      <MoveWorkspaceDialog
        open={dialog === 'workspace'}
        count={selected.length}
        workspaces={workspaces}
        busy={busy}
        error={error}
        onClose={() => setDialog(null)}
        onSave={(workspaceId) => void run('move_workspace', { workspace_id: workspaceId })}
      />
      <ConfirmDialog
        open={dialog === 'archive'}
        onClose={() => setDialog(null)}
        title={`Archive ${plural(selected.length, 'document')}?`}
        tone="danger"
        confirmLabel="Archive"
        cancelLabel="Keep them"
        busy={busy}
        error={error}
        onConfirm={() => void run('archive', {})}
      >
        <p>They leave every list, count and filter for everyone in this organisation; an administrator can restore each one.</p>
        <p>Nothing is deleted: the files, their versions and their history stay until the organisation’s retention rule decides.</p>
      </ConfirmDialog>
    </div>
  )
}

function TagDialog({
  open,
  count,
  tags,
  canCreate,
  busy,
  error,
  onClose,
  onSave,
}: {
  open: boolean
  count: number
  tags: ReturnType<typeof useTags>
  canCreate: boolean
  busy: boolean
  error: string
  onClose: () => void
  onSave: (mode: 'tag' | 'untag', value: TagSelection) => void
}) {
  const [mode, setMode] = useState<'tag' | 'untag'>('tag')
  const [value, setValue] = useState<TagSelection>({ tag_ids: [], new_names: [] })
  useEffect(() => {
    if (open) {
      setMode('tag')
      setValue({ tag_ids: [], new_names: [] })
    }
  }, [open])
  const empty = value.tag_ids.length === 0 && value.new_names.length === 0
  return (
    <Dialog open={open} onClose={onClose} title={`Tag ${plural(count, 'document')}`} error={error}>
      <form
        className="space-y-5"
        onSubmit={(e) => {
          e.preventDefault()
          if (!empty) onSave(mode, mode === 'untag' ? { ...value, new_names: [] } : value)
        }}
      >
        <RadioGroup
          legend="What should happen"
          options={[
            { value: 'tag', label: 'Add these tags', description: 'Each document gets them once, whatever it carries already.' },
            { value: 'untag', label: 'Remove these tags', description: 'Each document loses them; other tags stay.' },
          ]}
          value={mode}
          onChange={(v) => setMode(v as 'tag' | 'untag')}
        />
        {tags.error && !tags.data ? (
          <Alert tone="error">Could not load the tags: {tags.error}</Alert>
        ) : (
          <TagPicker tags={tags.list} value={value} onChange={setValue} canCreate={canCreate && mode === 'tag'} />
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" loading={busy} disabled={empty}>
            {mode === 'tag' ? 'Add tags' : 'Remove tags'}
          </Button>
        </div>
      </form>
    </Dialog>
  )
}

function MoveFolderDialog({
  open,
  client,
  selected,
  workspaces,
  currentWorkspace,
  app,
  busy,
  error,
  onClose,
  onSave,
}: {
  open: boolean
  client: ProDocClient
  selected: ProDocDocument[]
  workspaces: Workspace[]
  currentWorkspace: string | null
  app: string
  busy: boolean
  error: string
  onClose: () => void
  onSave: (folderId: string | null) => void
}) {
  const spanned = [...new Set(selected.map((d) => d.workspace_id))]
  const initial = currentWorkspace ?? spanned[0] ?? null
  const [workspace, setWorkspace] = useState<string | null>(initial)
  const [folder, setFolder] = useState<string | null>(null)
  useEffect(() => {
    if (open) {
      setWorkspace(initial)
      setFolder(null)
    }
    // Reset on opening only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])
  const outside = selected.filter((d) => d.workspace_id !== workspace).length
  const name = workspaces.find((w) => w.id === workspace)?.name ?? 'this workspace'
  return (
    <Dialog open={open} onClose={onClose} title={`Move ${plural(selected.length, 'document')} to a folder`} error={error}>
      <form
        className="space-y-5"
        onSubmit={(e) => {
          e.preventDefault()
          onSave(folder)
        }}
      >
        {spanned.length > 1 && (
          <Select label="Workspace" value={workspace ?? ''} onChange={(e) => setWorkspace(e.target.value || null)} hint="A folder belongs to one workspace.">
            {spanned.map((id) => (
              <option key={id} value={id}>
                {workspaces.find((w) => w.id === id)?.name ?? 'Another workspace'}
              </option>
            ))}
          </Select>
        )}
        <FolderPicker client={client} workspaceId={workspace} value={folder} onChange={setFolder} label="Folder" rootLabel={`Top level of ${name}`} app={app} />
        {outside > 0 && (
          <Alert tone="info">
            {plural(outside, 'selected document')} {outside === 1 ? 'is' : 'are'} in another workspace and will not move: a document is filed only in a folder of its own workspace.
          </Alert>
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" loading={busy} disabled={!workspace}>
            Move
          </Button>
        </div>
      </form>
    </Dialog>
  )
}

function MoveWorkspaceDialog({
  open,
  count,
  workspaces,
  busy,
  error,
  onClose,
  onSave,
}: {
  open: boolean
  count: number
  workspaces: Workspace[]
  busy: boolean
  error: string
  onClose: () => void
  onSave: (workspaceId: string) => void
}) {
  const choices = workspaces.filter((w) => w.status !== 'archived')
  const [value, setValue] = useState('')
  useEffect(() => {
    if (open) setValue('')
  }, [open])
  return (
    <Dialog open={open} onClose={onClose} title={`Move ${plural(count, 'document')} to another workspace`} error={error}>
      <form
        className="space-y-5"
        onSubmit={(e) => {
          e.preventDefault()
          if (value) onSave(value)
        }}
      >
        <Select label="Workspace" value={value} onChange={(e) => setValue(e.target.value)} hint="They land in its top level, out of any folder; everyone who sees that workspace sees them from now on.">
          <option value="">Choose a workspace…</option>
          {choices.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}
              {w.is_default ? ' (default)' : ''}
            </option>
          ))}
        </Select>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" loading={busy} disabled={!value}>
            Move
          </Button>
        </div>
      </form>
    </Dialog>
  )
}
